/**
 * AddPersonAccountRoleModal — Phase 4b-2 commit 5 (2026-06-16, #11).
 *
 * The INVERSE of AddAccountPartyModal: here THIS PERSON is fixed and the
 * user picks the ACCOUNT (+ role). Opened from PersonDetailPage's Roles tab
 * ("Roles on accounts" → "+ Add role"); stages a CREATE op onto the page's
 * pending-account-party queue, committed through maker-checker on Save
 * (approach A1 — the page is one transaction).
 *
 * AccountParty has a simple schema (role + optional notes — no designation /
 * share / date), so this mirrors AddAccountPartyModal's slim field set.
 * Editing an existing account role still happens on its own detail page
 * (/account-parties/:id); this modal is create-only.
 *
 * C-113: rendered inside PersonDetailPage's <form id="person-form"> subtree,
 * so the inner submit must stopPropagation or it also fires the page Save.
 */
import {
  Alert, Button, Group, Modal, Select, Stack, Text, Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import * as React from 'react';

import { AccountPicker } from './EntityPicker';
import type { AccountPartyCreateInput, AccountPartyRole, UUID } from '../api/types';

const ROLE_OPTS: { value: AccountPartyRole; label: string }[] = [
  { value: 'JOINT_HOLDER', label: 'Joint Holder' },
  { value: 'INSURANCE_BENEFICIARY', label: 'Insurance Beneficiary' },
];

interface FormValues {
  account_id: string;
  role: AccountPartyRole;
  notes: string;
}

const EMPTY: FormValues = {
  account_id: '',
  role: 'JOINT_HOLDER',
  notes: '',
};

interface Props {
  opened: boolean;
  onClose: () => void;
  /** The person this role attaches — fixed (this page). */
  personId: UUID;
  personLabel: string;
  /** Called with the built payload; the parent appends a CREATE op with a
   *  freshly-minted tempKey to its pending-account-party queue. */
  onStage: (payload: AccountPartyCreateInput) => void;
}

export function AddPersonAccountRoleModal({
  opened, onClose, personId, personLabel, onStage,
}: Props) {
  const form = useForm<FormValues>({
    initialValues: EMPTY,
    validate: {
      account_id: (v) => (v.length >= 32 ? null : 'Account required'),
    },
  });

  React.useEffect(() => {
    if (opened) form.setValues(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  function handleSave(values: FormValues) {
    const payload: AccountPartyCreateInput = {
      account_id: values.account_id,
      person_id: personId,
      role: values.role,
    };
    if (values.notes) payload.notes = values.notes;
    onStage(payload);
    onClose();
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    form.onSubmit(handleSave)(e);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add an account role for this person" size="lg">
      <form onSubmit={handleFormSubmit}>
        <Stack>
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={500}>{personLabel}</Text>
            <Text size="xs" c="dimmed">
              The new role is staged and only commits when you click Save
              Changes on the person page.
            </Text>
          </Alert>

          <AccountPicker
            label="Account"
            description="Pick the account to attach this person to in the selected role."
            value={form.values.account_id || null}
            onChange={(v) => form.setFieldValue('account_id', v ?? '')}
            error={form.errors.account_id as string | undefined}
          />

          <Select
            label="Role"
            data={ROLE_OPTS}
            value={form.values.role}
            onChange={(v) => v && form.setFieldValue('role', v as AccountPartyRole)}
            allowDeselect={false}
            required
          />

          <Textarea
            label="Notes"
            placeholder="Optional"
            value={form.values.notes}
            onChange={(e) => form.setFieldValue('notes', e.currentTarget.value)}
            autosize
            minRows={2}
          />

          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit">Stage role</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
